import {
  getStorage,
  ref,
  uploadBytesResumable,
  getDownloadURL
} from 'firebase/storage'
import { GeoPoint, serverTimestamp } from 'firebase/firestore'
import db from '../db/db'
import Database from '../types/Database'

const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const
const MAX_IMAGE_SIZE = 5 * 1024 * 1024 // 5 MB

export async function uploadDiveSiteImage(file: File): Promise<string> {
  if (
    !ALLOWED_IMAGE_TYPES.includes(
      file.type as (typeof ALLOWED_IMAGE_TYPES)[number]
    )
  ) {
    throw new Error(
      `Invalid file type: ${file.type}. Accepted: JPEG, PNG, WebP.`
    )
  }
  if (file.size > MAX_IMAGE_SIZE) {
    throw new Error('File too large. Maximum size is 5 MB.')
  }

  const storage = getStorage()
  const storagePath = `diveSites/${Date.now()}_${file.name}`
  const storageRef = ref(storage, storagePath)
  const uploadTask = uploadBytesResumable(storageRef, file, {
    contentType: file.type
  })

  return new Promise<string>((resolve, reject) => {
    uploadTask.on(
      'state_changed',
      null,
      error => reject(error),
      async () => {
        try {
          resolve(await getDownloadURL(uploadTask.snapshot.ref))
        } catch (err) {
          reject(err)
        }
      }
    )
  })
}

export async function getAllDiveSites(): Promise<Database.DiveSite[]> {
  return (await db.query({
    collection: 'diveSites',
    orderBy: ['name', 'asc'],
    limit: 500
  })) as Database.DiveSite[]
}

export async function getDiveSiteByName(
  name: string
): Promise<Database.DiveSite | null> {
  const results = (await db.query({
    collection: 'diveSites',
    where: ['name', '==', name],
    limit: 1
  })) as Database.DiveSite[]
  return results[0] ?? null
}

export async function createDiveSite(data: {
  name: string
  address: string
  location: { lat: number; lng: number }
  pictureUrl?: string | null
  description: string
  depthTier?: '18m' | '30m' | '30m+' | null
}): Promise<string> {
  const result = await db.addItem('diveSites', {
    name: data.name,
    address: data.address,
    location: new GeoPoint(data.location.lat, data.location.lng),
    pictureUrl: data.pictureUrl || null,
    description: data.description,
    depthTier: data.depthTier ?? '18m',
    createdAt: serverTimestamp()
  })
  return result.id
}

export async function updateDiveSite(
  id: string,
  data: Partial<{
    name: string
    address: string
    location: { lat: number; lng: number }
    pictureUrl: string | null
    description: string
    depthTier: '18m' | '30m' | '30m+' | null
  }>
): Promise<void> {
  const update: Record<string, unknown> = { ...data }
  if (data.location) {
    update.location = new GeoPoint(data.location.lat, data.location.lng)
  }
  await db.updateItem('diveSites', id, update as any)
}

export async function deleteDiveSite(id: string): Promise<void> {
  await db.deleteItem('diveSites', id)
}
